import { useQuery } from "@tanstack/react-query";
import { Select, Spin } from "antd";
import { useState, useEffect } from "react";
import { FetchInquiryNumber_api } from "../../Api";
import { useDebounce } from "use-debounce";

const { Option } = Select;

interface Inquiry {
  id: string;
  inquiryNumber: string;
}

interface MultiInquirySelectorProps {
  selectedInquiries: Inquiry[];
  setSelectedInquiries: (inquiries: Inquiry[]) => void;
  totalAmount: number;
  setTotalAmount: (amount: number) => void;
}

export const MultiInquirySelector = ({
  selectedInquiries,
  setSelectedInquiries,
  totalAmount,
  setTotalAmount,
}: MultiInquirySelectorProps) => {
  const [inquiryData, setInquiryData] = useState<Inquiry[]>([]);
  const [allFetchedInquiries, setAllFetchedInquiries] = useState<Inquiry[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch] = useDebounce(searchKeyword, 500);

  const { isFetching } = useQuery<any>({
    queryKey: ["initialInquiries"],
    queryFn: async () => {
      const response = await FetchInquiryNumber_api(null, true, 1, 10);
      const data = response.inquiryData || [];
      setInquiryData(data);
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  // Maintain a master list of all inquiries fetched or selected to ensure labels are always available
  useEffect(() => {
    setAllFetchedInquiries((prev) => {
      const combined = [...prev, ...inquiryData, ...selectedInquiries];
      // Use a Map for O(1) lookups and naturally unique keys
      const uniqueMap = new Map();
      combined.forEach((inq) => {
        if (inq && inq.id) uniqueMap.set(inq.id, inq);
      });
      return Array.from(uniqueMap.values());
    });
  }, [inquiryData, selectedInquiries]);

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
  };

  useEffect(() => {
    const fetchFromBE = async () => {
      const trimmed = searchKeyword.trim().toLowerCase();
      // Fetch matching results (or top 10 if empty)
      const response = await FetchInquiryNumber_api(
        trimmed || null,
        true,
        1,
        10,
      );
      setInquiryData(response.inquiryData || []);
    };

    // Use debounced value for remote search
    if (debouncedSearch !== undefined) {
      fetchFromBE();
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (selectedInquiries.length > 0) {
      const total = selectedInquiries.reduce((acc: number, inq: any) => {
        const amt = Number(inq.totalamount) || 0;
        const companyPaid = Number(inq.company_paid_amount) || 0;
        const isBatapola = Number(inq.isBatapola) || 0;
        const priceList = JSON.parse(inq.priceList);


        console.log(priceList);
        
        const documentCharge =
          isBatapola == 1 ? Number(priceList.document_charge) : 0;
        return acc + (amt - companyPaid - documentCharge);
      }, 0);

      setTotalAmount(total);

    } else {
      setTotalAmount(0);
    }
  }, [selectedInquiries]);

  const displayOptions = (() => {
    const uniqueMap = new Map();
    // Prioritize selected inquiries so they stay in the list
    selectedInquiries.forEach((inq) => uniqueMap.set(inq.id, inq));
    // Add current search/initial results
    inquiryData.forEach((inq) => {
      if (!uniqueMap.has(inq.id)) uniqueMap.set(inq.id, inq);
    });
    return Array.from(uniqueMap.values());
  })();

  return (
    <div className="flex flex-col w-full max-w-2xl">
      <label className="block text-sm mb-1 text-gray-800 font-medium">
        Select Inquiry Numbers
      </label>
      <Select
        mode="multiple"
        showSearch
        loading={isFetching}
        placeholder="Search and select inquiry numbers"
        className="w-full"
        onSearch={handleSearch}
        filterOption={false} // Disable default filter to use our custom logic
        value={selectedInquiries.map((inq) => inq.id)}
        onChange={(values: string[]) => {
          // Resolve IDs back to objects from our pool
          const selected = values
            .map((id) => allFetchedInquiries.find((inq) => inq.id === id))
            .filter((inq): inq is Inquiry => !!inq);
          setSelectedInquiries(selected);
        }}
        notFoundContent={
          isFetching ? (
            <div className="flex justify-center p-2">
              <Spin size="small" />
            </div>
          ) : (
            <div className="px-2 py-1 text-gray-500 text-center">
              No inquiries found
            </div>
          )
        }
      >
        {displayOptions
          .filter((inq: any) => inq.isPaidCompany != 1) // Filter out paid ones
          .map((inq) => (
            <Option key={inq.id} value={inq.id}>
              {inq.inquiryNumber}
            </Option>
          ))}
      </Select>

      {totalAmount > 0 && (
        <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-blue-800 text-sm font-semibold">
            Total Selected: LKR: {totalAmount.toLocaleString("en-US")}.00
          </p>
        </div>
      )}
    </div>
  );
};
